import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
    Box,
    BoxProps,
    Fab,
    Grid,
    GridProps,
    InternalStandardProps,
} from "@mui/material";
import {
    SortableContainer,
    SortableContainerProps,
    SortableElement,
} from "@lumel/react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSnackbar } from "JS/Helpers/Snackbar";
import qs from "qs";

import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useRouting } from "JS/React/Hooks/Routes";
import { useAppSelector } from "JS/Redux/Store";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { getDownloadingSpeed, getRemainingTime } from "JS/Helpers";
import { AppDivider } from "JS/React/Components/AppDivider";
import { QueueItems } from "./Components/QueueItems";
import { AppTypography } from "../../Components/AppTypography";
import { Queue } from "./Components/Queue";
import { Layout } from "../Layout";

const RootBox = styled(Box)(({ theme }) => ({
    color: `${theme.palette.common.black}`,
}));

const ContentTypography = styled(AppTypography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    color: `${theme.palette.common.black}`,
}));

const ListGrid = styled(Grid)(({ theme }) => ({
    overflowY: "auto",
    width: "100%",
    height: `calc(100vh - ${theme.footer.height} - 40px)`,
    paddingBottom: `calc(3*${theme.footer.height})`,
}));

const FabWrapper = styled(Box)(({ theme }) => ({
    position: "fixed",
    right: "10px",
    bottom: `calc(${theme.footer.height} + 10px + env(safe-area-inset-bottom, 0px))`,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
}));

const ProgressItemWrapper = styled(Box)(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === "light"
            ? `${theme.palette.grey[200]}`
            : `${theme.palette.background.paper}`,
}));

export interface SortableContainerQueueProps
    extends InternalStandardProps<GridProps> {
    items: any;
    isEdit: boolean;
    handleDeleteItem: (nid: string) => void;
    isDisableControls: boolean;
}

export interface DownloadingQueueItemProps {
    value: any;
    isEdit: boolean;
    isDownloading: boolean;
    isSortable: boolean;
    isDisableControls: boolean;
    handleDeleteItem: (nid: string) => void;
}

const DownloadingQueueItem = SortableElement(
    (
        props: DownloadingQueueItemProps & { ref?: React.Ref<HTMLDivElement> },
    ) => {
        const {
            isEdit,
            value,
            isDownloading,
            isSortable,
            isDisableControls,
            handleDeleteItem,
            ref,
        } = props;

        return (
            <Box ref={ref}>
                <Queue
                    isEdit={isEdit}
                    isDisableControls={isDisableControls}
                    type={value?.type}
                    nid={value?.nid}
                    name={
                        value?.type === "audioBundle" ||
                        value?.type === "giftAudioBundle"
                            ? value?.media_title
                            : value?.name
                    }
                    isSortable={isSortable}
                    isShowProgress={false}
                    isDownloading={isDownloading}
                    handleDeleteItem={handleDeleteItem}
                />
                <AppDivider />
            </Box>
        );
    },
) as unknown as React.FC<DownloadingQueueItemProps & { index: number }>;

const SortableContainerQueue = SortableContainer(
    (
        props: SortableContainerQueueProps & {
            ref?: React.Ref<HTMLDivElement>;
        },
    ) => {
        const {
            isEdit,
            items,
            isDisableControls,
            handleDeleteItem,
            ref,
            ...rest
        } = props;

        // First item is the one currently downloading and is not sortable;
        // the sortable set (and its 0-based indices) starts from the second item.
        const sortableItems = items?.slice(1) || [];

        return (
            <Grid {...rest} ref={ref}>
                {sortableItems.map((item, index) => (
                    <DownloadingQueueItem
                        key={item.nid}
                        index={index}
                        isDisableControls={isDisableControls}
                        isDownloading={false}
                        isEdit={isEdit}
                        value={item}
                        isSortable={items.length > 2}
                        handleDeleteItem={handleDeleteItem}
                    />
                ))}
            </Grid>
        );
    },
) as unknown as React.FC<SortableContainerQueueProps & SortableContainerProps>;

export interface DownloadingQueueProps
    extends InternalStandardProps<BoxProps> {}

export const DownloadingQueue = (props: DownloadingQueueProps) => {
    const { className, ...rest } = props;
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { enqueueSnackbar } = useSnackbar();
    const [downloadingQueue, setDownloadingQueue] = useState([]);
    const [isEdit, setEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [showDeleteConfirm, setShowDeleteConfim] = useState<boolean>(false);
    const [deleteItemNid, setDeleteItemNid] = useState<string>("");
    const downloadedContentNids = useAppSelector(
        (state) => state.download.downloadedContentNids,
    );
    const location = useLocation();

    const { homeStack, audiosStack, videosStack, coursesStack, moreStack } =
        useGlobalNavStack();

    const downloadState = useAppSelector((state) => state.download);
    const {
        inProgressNid,
        loadedContent,
        startTime,
        contentLength,
        downloadContentPercentage,
        downloadContentName,
    } = downloadState;

    const getDownloadQueue = () => {
        return localStorage.getItem("downloadQueue")
            ? JSON.parse(localStorage.getItem("downloadQueue"))
            : [];
    };
    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { contentType = "" } = parsedQueryString;

        return {
            contentType: contentType.toString(),
        };
    }, [location?.search]);

    const contentType = queryParams?.contentType;

    useEffect(() => {
        const downloadQueue = getDownloadQueue();

        if (
            downloadQueue?.length !== downloadingQueue?.length ||
            downloadQueue?.length === 0
        ) {
            setDownloadingQueue(downloadQueue);
        }

        if (
            !inProgressNid &&
            downloadQueue?.length > 1 &&
            downloadQueue[0]?.tries === 4
        ) {
            let tempQueue = [...downloadingQueue];
            tempQueue?.splice(0, 1);
            setDownloadingQueue(tempQueue);
        }

        if (
            !inProgressNid &&
            downloadQueue?.length === 1 &&
            (downloadedContentNids?.find(
                (x) =>
                    (x?.split("-").length === 3
                        ? `${x?.split("-")[1]}-${x?.split("-")[2]}`
                        : x?.split("-")[1]) === downloadQueue[0]?.nid,
            ) ||
                downloadQueue[0]?.tries === 4)
        ) {
            setDownloadingQueue([]);
        }
        setLoading(false);
    }, [inProgressNid, downloadContentName, downloadedContentNids]);

    const inProgress = (nid: string) => nid === inProgressNid;

    const handleEditAndSaveClick = () => {
        setEdit(!isEdit);
    };

    const onSortEnd = ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }) => {
        if (oldIndex !== newIndex) {
            const [first, ...sortable] = downloadingQueue;
            const sortedArray = [
                first,
                ...arrayMoveImmutable(sortable, oldIndex, newIndex),
            ];

            setDownloadingQueue(sortedArray);
            localStorage.setItem("downloadQueue", JSON.stringify(sortedArray));
        }
    };

    const handleDeleteConfirm = (nid: string) => {
        setDeleteItemNid(nid);
        setShowDeleteConfim(true);
    };

    const handleDeleteItem = () => {
        if (
            inProgressNid &&
            deleteItemNid !== inProgressNid &&
            downloadingQueue.findIndex((x) => x.nid === deleteItemNid) !== -1
        ) {
            let tempQueue = [...downloadingQueue];
            tempQueue.splice(
                tempQueue.findIndex((x) => x.nid === deleteItemNid),
                1,
            );
            setDownloadingQueue(tempQueue);
            setDeleteItemNid("");
            localStorage.setItem("downloadQueue", JSON.stringify(tempQueue));
            enqueueSnackbar(`${messaging.downloadingQueue.success}`, {
                variant: "success",
            });
        } else {
            if (
                !(
                    downloadingQueue.length > 2 &&
                    downloadingQueue.findIndex((x) => x.nid === deleteItemNid) >
                        1
                )
            ) {
                enqueueSnackbar(`${messaging.downloadingQueue.deleteError}`, {
                    variant: "warning",
                });
                setDeleteItemNid("");
            } else {
                let tempQueue = [...downloadingQueue];
                tempQueue.splice(
                    tempQueue.findIndex((x) => x.nid === deleteItemNid),
                    1,
                );
                setDownloadingQueue(tempQueue);
                setDeleteItemNid("");
                localStorage.setItem(
                    "downloadQueue",
                    JSON.stringify(tempQueue),
                );
                enqueueSnackbar(`${messaging.downloadingQueue.success}`, {
                    variant: "success",
                });
            }
        }
        handleCloseDeleteConfirm();
    };

    const handleCloseDeleteConfirm = () => {
        setDeleteItemNid("");
        setShowDeleteConfim(false);
    };

    const handleBackNavigation = () => {
        if (!homeStack?.isHomeTrack) {
            if (contentType === "Audio") {
                navigate(audiosStack?.lastVisited);
            } else if (contentType === "Video") {
                navigate(videosStack?.lastVisited);
            } else if (contentType === "gift") {
                navigate(-1);
            } else if (contentType === "Document") {
                navigate(moreStack?.lastVisited);
            } else if (contentType === "Course") {
                navigate(coursesStack?.lastVisited);
            } else {
                navigate(linkProvider.react.more().index());
            }
        } else {
            navigate(linkProvider.react.home());
        }
    };

    return (
        <Layout>
            <RootBox {...rest} className={className}>
                <AppHeader
                    title="Downloading Queue"
                    canGoBack
                    onBackClick={handleBackNavigation}
                    searchable={false}
                />
                {!loading &&
                    downloadingQueue &&
                    downloadingQueue.length > 0 && (
                        <>
                            <ListGrid>
                                <ProgressItemWrapper>
                                    <QueueItems
                                        contentType={downloadingQueue[0]?.type}
                                        isShowProgress={true}
                                        contentTitle={
                                            downloadingQueue[0]?.type ===
                                                "audioBundle" ||
                                            downloadingQueue[0]?.type ===
                                                "giftAudioBundle"
                                                ? downloadingQueue[0]
                                                      ?.media_title
                                                : downloadingQueue[0]?.name
                                        }
                                        isDownloading={inProgress(
                                            downloadingQueue[0]?.nid as string,
                                        )}
                                        downloadPercentage={
                                            inProgress(
                                                downloadingQueue[0]
                                                    ?.nid as string,
                                            )
                                                ? downloadContentPercentage
                                                : 0
                                        }
                                        status={downloadingQueue[0]?.status}
                                        timeRemaining={getRemainingTime(
                                            downloadingQueue[0]?.nid,
                                            inProgressNid,
                                            loadedContent,
                                            contentLength,
                                            startTime,
                                        )}
                                        downloadingSpeed={getDownloadingSpeed(
                                            downloadingQueue[0]?.nid,
                                            inProgressNid,
                                            loadedContent,
                                            contentLength,
                                            startTime,
                                        )}
                                    />
                                </ProgressItemWrapper>
                                <SortableContainerQueue
                                    key="sortableContainer"
                                    items={downloadingQueue}
                                    handleDeleteItem={handleDeleteConfirm}
                                    isEdit={isEdit}
                                    isDisableControls={inProgressNid === ""}
                                    useDragHandle
                                    lockAxis="y"
                                    distance={8}
                                    onSortEnd={onSortEnd}
                                />
                            </ListGrid>
                            <FabWrapper className={className}>
                                {downloadingQueue?.length > 1 && (
                                    <Fab
                                        color="primary"
                                        aria-label="edit"
                                        onClick={handleEditAndSaveClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={[
                                                "fas",
                                                isEdit
                                                    ? "circle-check"
                                                    : "pen-to-square",
                                            ]}
                                        />
                                    </Fab>
                                )}
                            </FabWrapper>
                            <AppConfimationDialog
                                open={showDeleteConfirm}
                                confirmText="Yes"
                                popupMainText={`${messaging.downloadingQueue.deleteConfirmation}`}
                                cancelButtonText="No"
                                onConfirm={handleDeleteItem}
                                onClose={handleCloseDeleteConfirm}
                                showConfirmFirst={true}
                            />
                        </>
                    )}
                {!loading &&
                    downloadingQueue &&
                    downloadingQueue.length === 0 && (
                        <ContentTypography align="center" variant="h6">
                            {messaging.common.noItemsFound}
                        </ContentTypography>
                    )}
            </RootBox>
        </Layout>
    );
};
